import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Eye, Trash2, ChevronDown, Loader2 } from 'lucide-react';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FooterActions,
  FormRow,
  FormGroup,
  InputLabel,
  TextInput,
  TextArea,
  SelectWrapper,
  StyledSelect,
  SelectChevron,
  ErrorText,
  CharCounterText,
  AttachmentsHeader,
  AddAttachmentButton,
  HiddenFileInput,
  AttachmentList,
  AttachmentItem,
  AttachmentName,
  AttachmentActions,
  IconActionButton,
  EmailChipsWrapper,
  EmailChip,
  EmailChipRemoveButton,
  ModalCancelButton,
  ModalSubmitButton,
} from './CreateTicketModal.styles';
import { useRaiseTicket } from '../hooks/useRaiseTicket';
import { useUploadTicketAttachment, isAllowedAttachment } from '../hooks/useUploadTicketAttachment';

const TICKET_TYPES = [
  { value: 'BUG', label: 'Bug' },
  { value: 'QUERY', label: 'Query' },
  { value: 'OTHER', label: 'Other' },
];

const TITLE_MAX_LENGTH = 150;
const QUERY_MAX_LENGTH = 1000;

let attachmentIdCounter = 0;
const nextAttachmentId = () => `att-${Date.now()}-${attachmentIdCounter++}`;

const CharCounter = ({ id, length, max }) => {
  const isAtLimit = length >= max;
  const isNearLimit = !isAtLimit && length >= max * 0.9;

  return (
    <CharCounterText id={id} isNearLimit={isNearLimit} isAtLimit={isAtLimit}>
      {length}/{max} characters
    </CharCounterText>
  );
};

const CreateTicketModal = ({ isOpen, onClose, folderName }) => {
  const [ticketType, setTicketType] = useState(TICKET_TYPES[0].value);
  const [title, setTitle] = useState('');
  const [query, setQuery] = useState('');

  const [attachments, setAttachments] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [errors, setErrors] = useState({});

  const fileInputRef = useRef(null);

  const uploadQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  const uploadAttachmentMutation = useUploadTicketAttachment();

  const raiseTicketMutation = useRaiseTicket({
    onSuccess: () => {
      resetState();
      onClose?.();
    },
    onError: (err) => {
      setErrors({ form: err?.message || 'Failed to raise the ticket. Please try again.' });
    },
  });

  const isSubmitting = raiseTicketMutation.isPending;

  const resetState = () => {
    setTicketType(TICKET_TYPES[0].value);
    setTitle('');
    setQuery('');
    // Revoke any object URLs we created for local previews.
    setAttachments((prev) => {
      prev.forEach((att) => att.previewUrl && URL.revokeObjectURL(att.previewUrl));
      return [];
    });
    uploadQueueRef.current = [];
    isProcessingQueueRef.current = false;
    setEmails([]);
    setEmailInput('');
    setErrors({});
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSubmitting]);

  if (!isOpen) return null;

  /* ---------------------------------------------------------------- */
  /* Title / Query change handlers                                     */
  /* ---------------------------------------------------------------- */

  const handleTitleChange = (event) => {
    const value = event.target.value.slice(0, TITLE_MAX_LENGTH);
    setTitle(value);
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: undefined }));
    }
  };

  const handleQueryChange = (event) => {
    const value = event.target.value.slice(0, QUERY_MAX_LENGTH);
    setQuery(value);
    if (errors.query) {
      setErrors((prev) => ({ ...prev, query: undefined }));
    }
  };

  /* ---------------------------------------------------------------- */
  /* Attachments                                                       */
  /* ---------------------------------------------------------------- */

  const handleAddAttachmentClick = () => {
    if (!folderName) return; // upload needs a folderName to sign against
    fileInputRef.current?.click();
  };

  const processUploadQueue = () => {
    if (isProcessingQueueRef.current) return;

    const next = uploadQueueRef.current.shift();
    if (!next) return;

    isProcessingQueueRef.current = true;
    const { id: localId, file } = next;

    setAttachments((prev) =>
      prev.map((att) => (att.id === localId ? { ...att, status: 'uploading' } : att))
    );

    uploadAttachmentMutation.mutate(
      { file, folderName },
      {
        onSuccess: (result) => {
          setAttachments((prev) =>
            prev.map((att) =>
              att.id === localId
                ? {
                    ...att,
                    status: 'done',
                    documentId: result.documentId,
                    fileSize: result.fileSize,
                    applicationType: result.applicationType,
                  }
                : att
            )
          );
        },
        // On failure: drop it from the list entirely and alert — no
        // lingering broken row the user has to clean up manually.
        onError: (err) => {
          setAttachments((prev) => {
            const failed = prev.find((att) => att.id === localId);
            if (failed?.previewUrl) URL.revokeObjectURL(failed.previewUrl);
            return prev.filter((att) => att.id !== localId);
          });
          window.alert(
            `Failed to upload "${file.name}"${
              err?.message ? `: ${err.message}` : '. Please try again.'
            }`
          );
        },
        onSettled: () => {
          isProcessingQueueRef.current = false;
          processUploadQueue(); // move on to the next queued file, if any
        },
      }
    );
  };

  const handleFileInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const rejected = files.filter((f) => !isAllowedAttachment(f));
    if (rejected.length) {
      window.alert('Only image and PDF files can be attached right now.');
    }
    const allowedFiles = files.filter(isAllowedAttachment);
    if (!allowedFiles.length) return;

    // Seed every row immediately (as "queued") so the user sees the full
    // batch right away, but the actual uploads run strictly one at a time
    // via processUploadQueue.
    const queued = allowedFiles.map((file) => ({
      id: nextAttachmentId(),
      file,
      name: file.name,
      previewUrl: URL.createObjectURL(file),
    }));

    setAttachments((prev) => [
      ...prev,
      ...queued.map(({ id, name, previewUrl }) => ({
        id,
        name,
        status: 'queued',
        previewUrl,
      })),
    ]);

    uploadQueueRef.current.push(...queued);
    processUploadQueue();
  };

  const handlePreviewAttachment = (attachment) => {
    if (!attachment.previewUrl) return;
    window.open(attachment.previewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRemoveAttachment = (attachment) => {
    if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    uploadQueueRef.current = uploadQueueRef.current.filter((item) => item.id !== attachment.id);
    setAttachments((prev) => prev.filter((item) => item.id !== attachment.id));
  };

  const commitEmail = () => {
    const value = emailInput.trim().replace(/,$/, '');
    if (value && /^\S+@\S+\.\S+$/.test(value) && !emails.includes(value)) {
      setEmails((prev) => [...prev, value]);
    }
    setEmailInput('');
  };

  const handleEmailKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitEmail();
    }
  };

  const handleRemoveEmail = (email) => {
    setEmails((prev) => prev.filter((item) => item !== email));
  };

  const hasPendingAttachments = attachments.some(
    (att) => att.status === 'uploading' || att.status === 'queued'
  );

  const handleSubmit = (event) => {
    event.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedQuery = query.trim();

    const nextErrors = {};

    if (!trimmedTitle) {
      nextErrors.title = 'A short title is required';
    } else if (trimmedTitle.length > TITLE_MAX_LENGTH) {
      nextErrors.title = `Title must be ${TITLE_MAX_LENGTH} characters or fewer`;
    }

    if (!trimmedQuery) {
      nextErrors.query = 'Please describe the issue';
    } else if (trimmedQuery.length > QUERY_MAX_LENGTH) {
      nextErrors.query = `Query must be ${QUERY_MAX_LENGTH} characters or fewer`;
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (hasPendingAttachments) return;

    raiseTicketMutation.mutate({
      title: trimmedTitle,
      ticketType,
      query: trimmedQuery,
      folderName,
      emailArray: emails,
      documentsArray: attachments
        .filter((att) => att.status === 'done')
        .map((att) => ({
          name: att.name,
          documentId: att.documentId,
          fileSize: att.fileSize,
          applicationType: att.applicationType,
        })),
    });
  };

  return createPortal(
    <ModalOverlay role="presentation">
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-ticket-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="create-ticket-modal-title">Raise a ticket</ModalTitle>
          <ModalCloseButton type="button" onClick={handleClose} aria-label="Close dialog">
            <X size={18} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormRow>
              <FormGroup>
                <InputLabel htmlFor="ticketType">Ticket type</InputLabel>
                <SelectWrapper>
                  <StyledSelect
                    id="ticketType"
                    value={ticketType}
                    onChange={(event) => setTicketType(event.target.value)}
                  >
                    {TICKET_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </StyledSelect>
                  <SelectChevron>
                    <ChevronDown />
                  </SelectChevron>
                </SelectWrapper>
              </FormGroup>

              <FormGroup>
                <InputLabel htmlFor="ticketTitle">Title</InputLabel>
                <TextInput
                  id="ticketTitle"
                  type="text"
                  value={title}
                  onChange={handleTitleChange}
                  maxLength={TITLE_MAX_LENGTH}
                  placeholder="Short summary"
                  $hasError={!!errors.title}
                  aria-describedby={errors.title ? 'ticket-title-error' : 'ticket-title-counter'}
                />
                {errors.title && (
                  <ErrorText id="ticket-title-error" role="alert">
                    {errors.title}
                  </ErrorText>
                )}
                <CharCounter
                  id="ticket-title-counter"
                  length={title.length}
                  max={TITLE_MAX_LENGTH}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <InputLabel htmlFor="queryDescription">Query</InputLabel>
              <TextArea
                id="queryDescription"
                rows={4}
                value={query}
                onChange={handleQueryChange}
                maxLength={QUERY_MAX_LENGTH}
                placeholder="Describe the issue in detail"
                aria-describedby={
                  errors.query ? 'query-description-error' : 'query-description-counter'
                }
              />
              {errors.query && (
                <ErrorText id="query-description-error" role="alert">
                  {errors.query}
                </ErrorText>
              )}
              <CharCounter
                id="query-description-counter"
                length={query.length}
                max={QUERY_MAX_LENGTH}
              />
            </FormGroup>

            <FormGroup>
              <AttachmentsHeader>
                <InputLabel as="span">Attachments</InputLabel>
                <AddAttachmentButton
                  type="button"
                  onClick={handleAddAttachmentClick}
                  disabled={!folderName}
                  title={!folderName ? 'Attachments are unavailable right now' : undefined}
                >
                  <Plus />
                  Add attachment
                </AddAttachmentButton>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  multiple
                  onChange={handleFileInputChange}
                />
              </AttachmentsHeader>

              {attachments.length > 0 && (
                <AttachmentList>
                  {attachments.map((attachment) => {
                    const isPending =
                      attachment.status === 'uploading' || attachment.status === 'queued';

                    return (
                      <AttachmentItem key={attachment.id}>
                        <AttachmentName title={attachment.name}>
                          {attachment.name}
                          {attachment.status === 'queued' && ' (queued…)'}
                          {attachment.status === 'uploading' && ' (uploading…)'}
                        </AttachmentName>
                        <AttachmentActions>
                          {isPending ? (
                            <Loader2
                              size={15}
                              style={{ animation: 'spin 1s linear infinite', opacity: 0.7 }}
                            />
                          ) : (
                            <IconActionButton
                              type="button"
                              title={`Preview ${attachment.name}`}
                              aria-label={`Preview ${attachment.name}`}
                              onClick={() => handlePreviewAttachment(attachment)}
                            >
                              <Eye />
                            </IconActionButton>
                          )}
                          <IconActionButton
                            type="button"
                            $danger
                            title={`Remove ${attachment.name}`}
                            aria-label={`Remove ${attachment.name}`}
                            onClick={() => handleRemoveAttachment(attachment)}
                          >
                            <Trash2 />
                          </IconActionButton>
                        </AttachmentActions>
                      </AttachmentItem>
                    );
                  })}
                </AttachmentList>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="notifyEmailInput">CC to Email Address</InputLabel>
              <TextInput
                id="notifyEmailInput"
                type="text"
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                onKeyDown={handleEmailKeyDown}
                onBlur={commitEmail}
                placeholder="Type an email and press enter"
              />
              {emails.length > 0 && (
                <EmailChipsWrapper>
                  {emails.map((email) => (
                    <EmailChip key={email}>
                      <span>{email}</span>
                      <EmailChipRemoveButton
                        type="button"
                        aria-label={`Remove ${email}`}
                        onClick={() => handleRemoveEmail(email)}
                      >
                        <X />
                      </EmailChipRemoveButton>
                    </EmailChip>
                  ))}
                </EmailChipsWrapper>
              )}
            </FormGroup>

            {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <FooterActions>
              <ModalCancelButton type="button" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </ModalCancelButton>
              <ModalSubmitButton type="submit" disabled={isSubmitting || hasPendingAttachments}>
                {isSubmitting && <Loader2 style={{ animation: 'spin 1s linear infinite' }} />}
                {isSubmitting ? 'Raising…' : 'Raise a ticket'}
              </ModalSubmitButton>
            </FooterActions>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default CreateTicketModal;
